import { HttpClient } from "@angular/common/http";
import { AfterViewInit, Component, Injector, OnDestroy } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { debounceTime } from "rxjs/internal/operators/debounceTime";
import { take, takeUntil } from "rxjs/operators";
import publicHelpers from "src/app/helpers/public-helpers";
import { UpdateQuestionOutput } from "../entities/custom-questions.component";
import { SocialLinksData, SocialLinksDialogComponent } from "../social-links/socialLinks.dialog.component";
import { EntityBase } from "globals/store/entity.shared.data";
import { getXYFromEventAndWidth, sleepForMS } from '../../helpers/helpers';
import { getJoiTemplate } from "globals/store/publish.data";
import { MobileAppService } from "./mobile.app.service";
import { MobileAppState, MobileStoreWithDestroy } from "./mobile-store/mobile.app.store";
import { MemoizedSelectorWithProps, Store } from "@ngrx/store";
import { MobileAppSubpage, mobilePageNames } from "./mobile-store/mobile.data";
import { ProgramTab } from "globals/store/share.data";

export class MobileAppServices {
  static store: Store<MobileAppState>
  static router: Router
  static dialog: MatDialog
  static mobileAppService: MobileAppService
  static http: HttpClient
  static initialize(injector: Injector) {
    MobileAppServices.http = injector.get(HttpClient)
    MobileAppServices.router = injector.get(Router)
    MobileAppServices.dialog = injector.get(MatDialog)
    MobileAppServices.mobileAppService = injector.get(MobileAppService)
    MobileAppServices.store = injector.get(Store)
  }
}

@Component({
    template: '',
    standalone: true,
    imports: []
})
export class MobileAppWrapper<Child> implements OnDestroy, MobileStoreWithDestroy, AfterViewInit {
  $destroy: Subject<boolean> = new Subject()
  helpers = publicHelpers
  error: string
  ERROR_DISPLAY_MS = 5000
  loading: boolean
  processing: boolean
  submitted = false
  ready = false
  template = getJoiTemplate()
  imageHints = ['JPG, PNG, GIF or SVG', '10MB maximum']
  public get dialog():  MatDialog { return MobileAppServices.dialog }
  public get router(): Router { return MobileAppServices.router }
  public get mobileAppService(): MobileAppService { return MobileAppServices.mobileAppService }
  public get http(): HttpClient { return MobileAppServices.http }
  public get store(): Store<MobileAppState> { return MobileAppServices.store }


  mobileAppSubpageToTabId: Partial<{ [id in MobileAppSubpage]: ProgramTab }> = { home: 'homeTab', program: 'programTab', performers: 'performersTab', sponsors: 'sponsorsTab', exhibitors: 'exhibitorsTab' }
  pageIcons: { [s in MobileAppSubpage]: string } = { home: 'HOME', notifications: 'MOBILE_BELL', feedback: 'MOBILE_THUMB', options: 'HAMBURGER', program: 'PROGRAMS', agenda: 'MY_SCHEDULE48',
    performers: 'PERFORMERS48', sponsors: 'SPONSOR48', exhibitors: 'EXHIBITORS_24', privacy: 'EVENT_INFORMATION48', URL: 'SOCIAL_WEB' }
  pageNames = mobilePageNames

  constructor() {
  }

  ngAfterViewInit() { setTimeout(() => this.ready = true, 10) } // minor delay needed for rendering, not sure if 10 ms is sufficient for most browsers
  ngOnDestroy() { this.$destroy.next(true); this.$destroy.complete() }

  openDialogGetResult = <T extends unknown, C = any>(component: any, config?: MatDialogConfig): Promise<T> => this.dialog.open(component, config).afterClosed().toPromise()

  subObservableWithDebounce = <T>(obs: Observable<T>, debounceMS: number, callback: (val: T) => void, destroySubject: Subject<boolean> = null) => 
    obs.pipe(debounceTime(debounceMS)).pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => callback(val))

  take1 = <T>(obs: Observable<T>): Promise<T> =>
    obs.pipe(take(1)).toPromise()

  subCallback = <T> (obs: Observable<T>, callback: (val: T) => void, destroySubject: Subject<boolean> = null) => 
    obs.pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => callback(val))

  subProp = <T extends Child[K], K extends keyof Child> (obs: Observable<T>, prop: K, destroySubject: Subject<boolean> = null) => 
    obs.pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => (this as unknown as Child)[prop] = val)

  // props refers to the type passed to the reselector as the extra prop input
  selectCallback = <T, P>(reselector: MemoizedSelectorWithProps<MobileAppState, P, T>, props: P, callback: (val: T) => void, destroySubject: Subject<boolean> = null) => 
    MobileAppServices.store.select(reselector, props).pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => callback(val))

  // props refers to the type passed to the reselector as the extra prop input
  selectProp = <T extends Child[K], K extends keyof Child, P>(reselector: MemoizedSelectorWithProps<MobileAppState, P, T>, props: P, propOrCallback: K, destroySubject: Subject<boolean> = null) => 
    MobileAppServices.store.select(reselector, props).pipe(takeUntil(destroySubject || this.$destroy)).subscribe((val: T) => (this as unknown as Child)[propOrCallback] = val
  )

  trackByIndex(index) {
    return index
  }

  trackById<T extends { id?: string }>(_, item: T) {
    return item.id
  }

  trackBySelf<T>(_, item: T) {
    return item // in case of primitive, compares primitives.  Objects compare references
  }

  public scrollUp() {
    document.querySelector("html").scrollTo(0,0)
  }
  

  async showError(errorMessage: string) {
    this.error = errorMessage
    await sleepForMS(this.ERROR_DISPLAY_MS)
    this.error = null
  } 

  async openSocialLinks(event, entity: EntityBase) {
    const data: SocialLinksData = { social: entity.social, ...getXYFromEventAndWidth(event, 300), template: this.template }
    await this.openDialogGetResult(SocialLinksDialogComponent, { width: '300px', data, disableClose: true, panelClass: 'vertical-center' })
    entity.social = data.social
  }

  updateCustomQuestion(data: UpdateQuestionOutput, entity: EntityBase) {
    const { id, answer } = data
    entity.customQuestions[id] = answer
  }

}
